/**
 * Header Email Sidebar Service
 * Manages state and real-time communication for the email sidebar triggered from header
 *
 * Features:
 * - Sidebar visibility toggle
 * - Unread email count with real-time updates
 * - WebSocket integration for new email notifications
 * - Email quick actions (mark as read, flag, delete)
 * - Integration with existing EmailCommunicationService
 */

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Observable, interval, Subscription } from 'rxjs';
import { takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IUserAccount } from '../../../email/data/email-dummy-data';
import { IEmailFolder } from '../../../email/interfaces/email-folder.interface';
import { IEmailAttachment } from '../../../email/interfaces/email-message.interface';
import { UserService } from '../../../../services/core/user.service';

export interface IQuickEmail {
  id: number;
  contactId: string;
  senderName: string;
  senderEmail: string;
  senderInitials: string;
  senderAvatarColor: string;
  subject: string;
  preview: string;
  time: string;
  timeAgo: string;
  isRead: boolean;
  isFlagged: boolean;
  hasAttachment: boolean;
  attachments?: IEmailAttachment[];
  folder: string;
  priority: 'high' | 'normal' | 'low';
  // Additional fields for compose/reply operations
  message?: string;           // Full email body for reply quoting
  ccRecipients?: Array<{ name: string; email: string }>;
  date?: string | Date;       // For forwarded message header
  // Thread/conversation fields
  threadId?: string;
  threadMessageCount?: number;
  emailType?: string;         // 'inbox' or 'outbox'
  messageId?: string;
}

export interface IEmailNotification {
  id: number;
  emailId: number;
  type: 'new' | 'reply' | 'mention';
  title: string;
  message: string;
  senderName: string;
  senderEmail: string;
  timestamp: Date;
  isRead: boolean;
}

export type EmailSidebarTab = 'inbox' | 'unread' | 'flagged' | 'notifications';

@Injectable({
  providedIn: 'root'
})
export class HeaderEmailSidebarService implements OnDestroy {

  // ==================== SIDEBAR VISIBILITY ====================

  private sidebarVisibleSubject = new BehaviorSubject<boolean>(false);
  public sidebarVisible$ = this.sidebarVisibleSubject.asObservable();

  // ==================== ACTIVE TAB ====================

  private activeTabSubject = new BehaviorSubject<EmailSidebarTab>('inbox');
  public activeTab$ = this.activeTabSubject.asObservable();

  // ==================== EMAIL DATA ====================

  private emailsSubject = new BehaviorSubject<IQuickEmail[]>([]);
  public emails$ = this.emailsSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<IEmailNotification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  // ==================== COUNTS ====================

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private flaggedCountSubject = new BehaviorSubject<number>(0);
  public flaggedCount$ = this.flaggedCountSubject.asObservable();

  private notificationCountSubject = new BehaviorSubject<number>(0);
  public notificationCount$ = this.notificationCountSubject.asObservable();

  // ==================== LOADING STATE ====================

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  // ==================== ACCOUNT MANAGEMENT ====================

  private activeAccountSubject = new BehaviorSubject<IUserAccount | null>(null);
  public activeAccount$ = this.activeAccountSubject.asObservable();
  public userAccounts: IUserAccount[] = [];

  // ==================== SEARCH ====================

  private searchQuerySubject = new BehaviorSubject<string>('');
  public searchQuery$ = this.searchQuerySubject.asObservable();

  // ==================== FOLDER MANAGEMENT ====================

  private activeFolderSubject = new BehaviorSubject<IEmailFolder | null>(null);
  public activeFolder$ = this.activeFolderSubject.asObservable();

  // Filtered emails based on search and active tab
  public filteredEmails$: Observable<IQuickEmail[]> = this.emails$.pipe(
    map(emails => this.filterEmails(emails))
  );

  // ==================== REAL-TIME ====================

  private newEmailSubject = new Subject<IQuickEmail>();
  public newEmail$ = this.newEmailSubject.asObservable();

  private destroy$ = new Subject<void>();
  private pollingSubscription: Subscription | null = null;
  private webSocket: WebSocket | null = null;

  // ==================== CONFIGURATION ====================

  private readonly POLL_INTERVAL = 30000; // 30 seconds
  private readonly MAX_QUICK_EMAILS = 20;
  private readonly API_BASE = environment.apiUrl;

  // Avatar color palette
  private readonly AVATAR_COLORS = [
    '#FF6600', '#28304E', '#4CAF50', '#2196F3', '#9C27B0',
    '#FF5722', '#607D8B', '#795548', '#E91E63', '#00BCD4'
  ];

  private apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}`;

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {
    this.initializeRealTimeUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
    this.disconnectWebSocket();
  }

  // ==================== SIDEBAR VISIBILITY ====================

  toggleSidebar(): void {
    const newValue = !this.sidebarVisibleSubject.getValue();
    this.sidebarVisibleSubject.next(newValue);

    if (newValue) {
      this.loadEmails();
    }
  }

  showSidebar(): void {
    this.sidebarVisibleSubject.next(true);
    this.loadEmails();
  }

  hideSidebar(): void {
    this.sidebarVisibleSubject.next(false);
  }

  isSidebarVisible(): boolean {
    return this.sidebarVisibleSubject.getValue();
  }

  // ==================== TAB MANAGEMENT ====================

  setActiveTab(tab: EmailSidebarTab): void {
    this.activeTabSubject.next(tab);
  }

  getActiveTab(): EmailSidebarTab {
    return this.activeTabSubject.getValue();
  }

  // ==================== SEARCH ====================

  setSearchQuery(query: string): void {
    this.searchQuerySubject.next(query);
  }

  clearSearch(): void {
    this.searchQuerySubject.next('');
  }

  // ==================== FOLDER MANAGEMENT METHODS ====================

  setActiveFolder(folder: IEmailFolder | null): void {
    this.activeFolderSubject.next(folder);
    this.loadEmails(); // Reload with folder filter
  }

  getActiveFolder(): IEmailFolder | null {
    return this.activeFolderSubject.getValue();
  }

  resetFolder(): void {
    this.activeFolderSubject.next(null);
  }

  // ==================== ACCOUNT MANAGEMENT METHODS ====================

  initializeAccounts(): void {
    const userId = this.userService.getID();
    if (!userId) {
      this.userAccounts = [];
      return;
    }

    this.http.get(`${this.apiUrl}/emails/user-accounts/${userId}?fields=minimal`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data?.accounts) {
            this.userAccounts = response.data.accounts.map((account: any) => ({
              id: account.id,
              name: account.display_name || account.email_type || account.email_address,
              email: account.email_address,
              avatarUrl: '',
              isExpanded: account.is_primary,
              folders: [],
              accountId: account.id,
              emailType: account.email_type
            }));

            // Sort: primary account first
            this.userAccounts.sort((a: any, b: any) => {
              if (a.isExpanded && !b.isExpanded) return -1;
              if (!a.isExpanded && b.isExpanded) return 1;
              return 0;
            });

            // Set active account (primary or first)
            const activeAccount = this.userAccounts.find((a: any) => a.isExpanded) || this.userAccounts[0];
            if (activeAccount && !this.activeAccountSubject.getValue()) {
              this.activeAccountSubject.next(activeAccount);
              // Load folders for the active account
              this.loadAccountFolders(activeAccount);
            }
          } else {
            this.userAccounts = [];
          }
        },
        error: (error) => {
          console.error('Failed to load email accounts:', error);
          this.userAccounts = [];
        }
      });
  }

  /**
   * Load folders for a specific email account from the API.
   * The backend already returns folders in IEmailFolder-compatible format
   * (ID, PID, Name, Count, HasChild, folderType, isFavorite, unreadCount, etc.)
   */
  loadAccountFolders(account: IUserAccount): void {
    const accountId = (account as any).accountId || account.id;
    if (!accountId) return;

    this.http.get(`${this.apiUrl}/emails/folders/${accountId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data?.folders) {
            // API response already has ID, PID, Name, Count, HasChild — use directly
            const folders: IEmailFolder[] = response.data.folders;

            // Update the account's folders
            account.folders = folders;

            // If this is the active account, notify subscribers
            const currentActive = this.activeAccountSubject.getValue();
            if (currentActive && ((currentActive as any).accountId || currentActive.id) === accountId) {
              this.activeAccountSubject.next({ ...currentActive, folders });

              // Auto-select Inbox so emails load on first open without needing a click
              const inbox = folders.find((f: any) => f.folderType === 'INBOX');
              if (inbox) {
                this.setActiveFolder(inbox);
              }
            }
          }
        },
        error: (error) => {
          console.error('Failed to load account folders:', error);
        }
      });
  }

  switchAccount(account: IUserAccount): void {
    this.activeAccountSubject.next(account);
    // Reset folder so the new account's Inbox auto-selects after folders load
    this.activeFolderSubject.next(null);
    // Load folders if not already loaded
    if (!account.folders || account.folders.length === 0) {
      this.loadAccountFolders(account);
    } else {
      // Folders already cached — auto-select Inbox right away
      const inbox = account.folders.find((f: any) => f.folderType === 'INBOX');
      if (inbox) {
        this.setActiveFolder(inbox);
        return;
      }
    }
    this.loadEmails(); // Reload emails for new account (inbox by default)
  }

  getActiveAccount(): IUserAccount | null {
    return this.activeAccountSubject.getValue();
  }

  // ==================== EMAIL OPERATIONS ====================

  loadEmails(): void {
    const activeAccount = this.activeAccountSubject.getValue();
    const accountId = (activeAccount as any)?.accountId || activeAccount?.id;
    if (!accountId) {
      this.emailsSubject.next([]);
      this.updateCounts([]);
      return;
    }

    this.loadingSubject.next(true);

    const activeFolder = this.activeFolderSubject.getValue();
    let params: any = { email_account_id: accountId };

    // Determine which API to call based on folder type
    let apiUrl = `${this.apiUrl}/emails/inbox`;

    if (activeFolder) {
      const folderType = (activeFolder as any).folderType;
      if (folderType === 'SENT' || folderType === 'DRAFTS') {
        apiUrl = `${this.apiUrl}/emails/outbox`;
      } else if (folderType === 'SPAM') {
        apiUrl = `${this.apiUrl}/emails/junk`;
      } else if (folderType === 'TRASH') {
        apiUrl = `${this.apiUrl}/emails/deleted`;
      }
      // For custom/other folders, pass email_folder_id
      if (activeFolder.ID) {
        params.email_folder_id = activeFolder.ID;
      }
    }

    this.http.get(apiUrl, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success && response.data?.emails) {
            const emails = response.data.emails.map((email: any) => this.mapApiEmailToQuickEmail(email));
            this.emailsSubject.next(emails);
            this.updateCounts(emails);
          } else {
            this.emailsSubject.next([]);
            this.updateCounts([]);
          }
          this.loadingSubject.next(false);
        },
        error: (error) => {
          console.error('Failed to load emails:', error);
          this.emailsSubject.next([]);
          this.updateCounts([]);
          this.loadingSubject.next(false);
        }
      });
  }

  /**
   * Map API inbox/outbox email response to IQuickEmail format for sidebar display.
   * For outbox (sent) emails, shows recipient ("To: ...") instead of sender.
   */
  private mapApiEmailToQuickEmail(email: any): IQuickEmail {
    const isOutbox = email.type === 'outbox';
    const receivedAt = email.received_at || email.sent_at || email.created_at;

    // For sent items: show first recipient as display name (matches email module pattern)
    // For inbox: show sender name
    let displayName: string;
    let displayEmail: string;

    if (isOutbox) {
      const firstRecipient = email.to_emails && Array.isArray(email.to_emails) && email.to_emails.length > 0
        ? email.to_emails[0]
        : '';
      displayName = firstRecipient ? `To: ${firstRecipient}` : 'To: (No recipient)';
      displayEmail = firstRecipient || '';
    } else {
      displayName = email.from_name || email.from_email || 'Unknown';
      displayEmail = email.from_email || '';
    }

    return {
      id: email.id,
      contactId: email.message_id || `email-${email.id}`,
      senderName: displayName,
      senderEmail: displayEmail,
      senderInitials: this.getSenderInitials(displayName.replace(/^To:\s*/, '')),
      senderAvatarColor: this.getAvatarColor(displayEmail),
      subject: email.subject || '(No Subject)',
      preview: email.snippet || email.body_text?.substring(0, 150) || '',
      message: email.body_html || email.body_text || '',
      time: receivedAt ? new Date(receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
      timeAgo: receivedAt ? this.formatTimeAgo(receivedAt) : '',
      isRead: !!email.is_read,
      isFlagged: email.flag_status === 'flagged' || email.flag_status === 'complete',
      hasAttachment: (email.attachments && email.attachments.length > 0) || false,
      attachments: email.attachments || [],
      folder: email.folder || (isOutbox ? 'Sent' : 'Inbox'),
      priority: email.importance === 'high' ? 'high' : 'normal',
      date: receivedAt ? new Date(receivedAt) : new Date(),
      ccRecipients: email.cc_emails ? (Array.isArray(email.cc_emails)
        ? email.cc_emails.map((cc: any) => typeof cc === 'string' ? { name: cc, email: cc } : cc)
        : []) : [],
      emailType: email.type || 'inbox',
      messageId: email.message_id,
      threadId: email.thread_id || null,
      threadMessageCount: email.thread_message_count || 1,
    } as any;
  }

  refreshEmails(): void {
    this.loadEmails();
  }

  markAsRead(emailId: number): void {
    const emails = this.emailsSubject.getValue();
    const email = emails.find(e => e.id === emailId);
    if (!email) return;

    // Optimistic update
    const updatedEmails = emails.map(e => e.id === emailId ? { ...e, isRead: true } : e);
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);

    // API call
    this.http.post(`${this.apiUrl}/emails/${emailId}/mark-read`, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => console.error('Mark as read failed:', err) });
  }

  markAsUnread(emailId: number): void {
    const emails = this.emailsSubject.getValue();

    // Optimistic update
    const updatedEmails = emails.map(e => e.id === emailId ? { ...e, isRead: false } : e);
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);

    // API call
    this.http.post(`${this.apiUrl}/emails/${emailId}/mark-unread`, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => console.error('Mark as unread failed:', err) });
  }

  toggleFlag(emailId: number): void {
    const emails = this.emailsSubject.getValue();
    const email = emails.find(e => e.id === emailId);
    if (!email) return;

    const newFlagState = email.isFlagged ? 'notFlagged' : 'flagged';

    // Optimistic update
    const updatedEmails = emails.map(e => e.id === emailId ? { ...e, isFlagged: !e.isFlagged } : e);
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);

    // API call — requires 'type' field (inbox/outbox)
    const emailType = (email as any).emailType || 'inbox';
    this.http.patch(`${this.apiUrl}/emails/flag/${emailId}`, { flag_status: newFlagState, type: emailType })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => console.error('Toggle flag failed:', err) });
  }

  deleteEmail(emailId: number): void {
    // Optimistic update
    const emails = this.emailsSubject.getValue();
    const updatedEmails = emails.filter(e => e.id !== emailId);
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);

    // API call
    this.http.delete(`${this.apiUrl}/emails/${emailId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (err) => console.error('Delete email failed:', err) });
  }

  markAllAsRead(): void {
    const emails = this.emailsSubject.getValue();
    const updatedEmails = emails.map(email => ({ ...email, isRead: true }));
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);

    // Mark each unread email as read via API
    emails.filter(e => !e.isRead).forEach(e => {
      this.http.post(`${this.apiUrl}/emails/${e.id}/mark-read`, {})
        .pipe(takeUntil(this.destroy$))
        .subscribe({ error: (err) => console.error('Mark as read failed:', err) });
    });
  }

  // ==================== THREAD/CONVERSATION ====================

  /**
   * Fetch full thread (all inbox + outbox messages) for a conversation.
   * Uses GET /emails/thread/{threadId} — same API used by the email module.
   * Returns messages sorted chronologically (oldest first).
   */
  fetchThread(threadId: string): Observable<IQuickEmail[]> {
    return this.http.get(`${this.apiUrl}/emails/thread/${threadId}`).pipe(
      map((response: any) => {
        if (response.success && response.data?.emails) {
          return response.data.emails.map((email: any) => this.mapApiEmailToQuickEmail(email));
        }
        return [];
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Lazy-load email body (body_html excluded from list API for performance).
   * Uses GET /emails/body/{emailId}?type=inbox|outbox
   */
  fetchEmailBody(emailId: number, type: string = 'inbox'): Observable<string> {
    return this.http.get(`${this.apiUrl}/emails/body/${emailId}`, { params: { type } }).pipe(
      map((response: any) => {
        if (response.success && response.data) {
          return response.data.body_html || response.data.body_text || '';
        }
        return '';
      }),
      takeUntil(this.destroy$)
    );
  }

  // ==================== NOTIFICATION OPERATIONS ====================

  dismissNotification(notificationId: number): void {
    const notifications = this.notificationsSubject.getValue();
    const updatedNotifications = notifications.filter(n => n.id !== notificationId);
    this.notificationsSubject.next(updatedNotifications);
    this.notificationCountSubject.next(updatedNotifications.filter(n => !n.isRead).length);
  }

  markNotificationAsRead(notificationId: number): void {
    const notifications = this.notificationsSubject.getValue();
    const updatedNotifications = notifications.map(n => {
      if (n.id === notificationId) {
        return { ...n, isRead: true };
      }
      return n;
    });
    this.notificationsSubject.next(updatedNotifications);
    this.notificationCountSubject.next(updatedNotifications.filter(n => !n.isRead).length);
  }

  clearAllNotifications(): void {
    this.notificationsSubject.next([]);
    this.notificationCountSubject.next(0);
  }

  // ==================== COUNT MANAGEMENT ====================

  private updateCounts(emails: IQuickEmail[]): void {
    const unreadCount = emails.filter(e => !e.isRead).length;
    const flaggedCount = emails.filter(e => e.isFlagged).length;

    this.unreadCountSubject.next(unreadCount);
    this.flaggedCountSubject.next(flaggedCount);
  }

  getUnreadCount(): number {
    return this.unreadCountSubject.getValue();
  }

  getTotalUnreadCount(): number {
    return this.unreadCountSubject.getValue() + this.notificationCountSubject.getValue();
  }

  // ==================== REAL-TIME UPDATES ====================

  private initializeRealTimeUpdates(): void {
    // Try WebSocket first, fall back to polling
    this.connectWebSocket();

    // Start polling as backup
    this.startPolling();
  }

  private connectWebSocket(): void {
    // TODO: Implement WebSocket connection when backend is ready
    // const wsUrl = environment.wsUrl || 'ws://localhost:8080/ws/emails';
    //
    // try {
    //   this.webSocket = new WebSocket(wsUrl);
    //
    //   this.webSocket.onopen = () => {
    //     console.log('Email WebSocket connected');
    //     this.stopPolling(); // Stop polling when WS is connected
    //   };
    //
    //   this.webSocket.onmessage = (event) => {
    //     const data = JSON.parse(event.data);
    //     if (data.type === 'new_email') {
    //       this.handleNewEmail(data.email);
    //     } else if (data.type === 'email_update') {
    //       this.handleEmailUpdate(data);
    //     }
    //   };
    //
    //   this.webSocket.onclose = () => {
    //     console.log('Email WebSocket disconnected');
    //     this.startPolling(); // Resume polling on disconnect
    //     // Reconnect after 5 seconds
    //     setTimeout(() => this.connectWebSocket(), 5000);
    //   };
    //
    //   this.webSocket.onerror = (error) => {
    //     console.error('Email WebSocket error:', error);
    //   };
    // } catch (error) {
    //   console.error('Failed to connect WebSocket:', error);
    // }
  }

  private disconnectWebSocket(): void {
    if (this.webSocket) {
      this.webSocket.close();
      this.webSocket = null;
    }
  }

  private startPolling(): void {
    if (this.pollingSubscription) return;

    this.pollingSubscription = interval(this.POLL_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isSidebarVisible()) {
          this.refreshEmails();
        } else {
          // Just update counts
          this.pollUnreadCount();
        }
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private pollUnreadCount(): void {
    // TODO: Lightweight API call to get just the count
    // this.http.get<{count: number}>(`${this.API_BASE}/emails/unread-count`)
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe({
    //     next: (response) => {
    //       this.unreadCountSubject.next(response.count);
    //     }
    //   });
  }

  private handleNewEmail(email: IQuickEmail): void {
    // Add to top of list
    const emails = this.emailsSubject.getValue();
    const updatedEmails = [email, ...emails].slice(0, this.MAX_QUICK_EMAILS);
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);

    // Emit new email event for notifications
    this.newEmailSubject.next(email);

    // Create notification
    const notification: IEmailNotification = {
      id: Date.now(),
      emailId: email.id,
      type: 'new',
      title: 'New Email',
      message: email.subject,
      senderName: email.senderName,
      senderEmail: email.senderEmail,
      timestamp: new Date(),
      isRead: false
    };

    const notifications = this.notificationsSubject.getValue();
    this.notificationsSubject.next([notification, ...notifications].slice(0, 50));
    this.notificationCountSubject.next(this.notificationCountSubject.getValue() + 1);
  }

  private handleEmailUpdate(data: any): void {
    const emails = this.emailsSubject.getValue();
    const updatedEmails = emails.map(email => {
      if (email.id === data.emailId) {
        return { ...email, ...data.changes };
      }
      return email;
    });
    this.emailsSubject.next(updatedEmails);
    this.updateCounts(updatedEmails);
  }

  // ==================== FILTERING ====================

  private filterEmails(emails: IQuickEmail[]): IQuickEmail[] {
    const activeTab = this.activeTabSubject.getValue();
    const searchQuery = this.searchQuerySubject.getValue().toLowerCase();
    const activeFolder = this.activeFolderSubject.getValue();

    let filtered = emails;

    // Filter by folder first
    if (activeFolder) {
      filtered = filtered.filter(e => e.folder === activeFolder.Name);
    }

    // Filter by tab
    switch (activeTab) {
      case 'unread':
        filtered = filtered.filter(e => !e.isRead);
        break;
      case 'flagged':
        filtered = filtered.filter(e => e.isFlagged);
        break;
      case 'notifications':
        // Notifications tab shows notifications, not emails
        return [];
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(e =>
        e.senderName.toLowerCase().includes(searchQuery) ||
        e.senderEmail.toLowerCase().includes(searchQuery) ||
        e.subject.toLowerCase().includes(searchQuery) ||
        e.preview.toLowerCase().includes(searchQuery)
      );
    }

    return filtered;
  }

  // ==================== HELPER METHODS ====================

  getSenderInitials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(word => word[0]?.toUpperCase())
      .join('') || '?';
  }

  getAvatarColor(email: string): string {
    // Generate consistent color based on email
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    return this.AVATAR_COLORS[Math.abs(hash) % this.AVATAR_COLORS.length];
  }

  formatTimeAgo(date: Date | string): string {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

}
